import { BRAND } from "constants/brand";
import { Ring } from "@uiball/loaders";
import "assets/styles/table.scss";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Form } from "react-bootstrap";
import { Toast } from "primereact/toast";
import DeleteItem from "components/Ui/DeleteItem/DeleteItem";
import DeliveryOfferRow from "./DeliveryOfferRow/DeliveryOfferRow";
import AddDeliveryOffer from "./AddDeliveryOffer/AddDeliveryOffer";
import EditDeliveryOffer from "./EditDeliveryOffer/EditDeliveryOffer";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import {
    useGetDeliveryOffers,
    useEditDeliveryOffer,
    useDeleteDeliveryOffer,
} from "hooks/useDeliveryOffers";
import useCity from "hooks/useCity";
import SearchInput from "components/Ui/SearchInput/SearchInput";
import { palestineToday } from "utils/driverShiftWeek";
import {
    BENEFIT_OPTIONS,
    CUSTOMER_RULE_OPTIONS,
    OFFER_PRESETS,
    OFFER_STATE_PILL,
    filterOffers,
    presetOfferForm,
} from "./deliveryOfferModel";

const COLUMNS = [
    { id: "id", label: "#", width: "10px" },
    { id: "name", label: "الاسم", width: "120px" },
    // The id is from the time when an offer was an amount only. It stays, so
    // the column settings that an admin saved keep working.
    { id: "discount", label: "العرض", width: "100px" },
    // `wrap`: the cell can hold a sentence, so it wraps, and the head keeps
    // the column at its width. A cell that wraps is the first one that the
    // browser makes narrow.
    { id: "condition", label: "الشرط", width: "180px", wrap: true },
    { id: "fundedBy", label: "على حساب", width: "70px" },
    { id: "vendors", label: "المطاعم", width: "80px" },
    { id: "areas", label: "المناطق", width: "80px" },
    { id: "city", label: "المدينه", width: "80px" },
    { id: "period", label: "الفتره", width: "190px", wrap: true },
    { id: "active", label: "الحاله", width: "130px" },
    { id: "actions", label: "الاعدادات", width: "50px" },
];

// A filter of the controls row. Two of them share a row on a phone, and
// each one takes its part of a wide row.
const FILTER_STYLE = { flex: "1 1 150px", width: "auto" };

const DeliveryOffers = () => {
    const adminInfo = useSelector((state) => state.auth.userData);
    const { cities } = useCity();
    const location = useLocation();
    const navigate = useNavigate();
    // An other page can open this one for a city, and with a ready offer: the
    // restaurant form («إضافه توصيل مجاني لهذا المطعم») and the menu of a city.
    // The state of the route has it, so it is not in the address.
    const asked = location.state;
    const ownCity = adminInfo?.super_admin ? "" : (adminInfo?.cityId ?? "");

    const [cityFilter, setCityFilter] = useState(() => (asked?.cityId ? String(asked.cityId) : ""));
    const [start, setStart] = useState(() =>
        asked?.preset
            ? {
                  form: presetOfferForm(asked.preset, {
                      cityId: asked.cityId || ownCity,
                      vendor: asked.vendor,
                  }),
              }
            : null,
    );
    const [showAddForm, setShowAddForm] = useState(() => !!asked?.preset);
    const [showEditForm, setShowEditForm] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const { offers, loading } = useGetDeliveryOffers();

    // The request of the other page is used one time, when this page opens.
    // A reload of this page must not open the form again.
    useEffect(() => {
        if (asked) {
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    /** Opens the add form with a ready offer, in the city of the filter. */
    const openPreset = (key) => {
        if (!OFFER_PRESETS[key]) return;
        setStart({ form: presetOfferForm(key, { cityId: cityFilter || ownCity }) });
        setShowAddForm(true);
    };

    // The filters of the page. The list is small, so they work on the rows
    // that the page has. The day is the Palestine date, as the API reads the
    // dates of an offer.
    const [stateFilter, setStateFilter] = useState("");
    const [benefitFilter, setBenefitFilter] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [search, setSearch] = useState("");
    const today = palestineToday();
    const shownOffers = filterOffers(
        offers,
        {
            search,
            cityId: cityFilter,
            state: stateFilter,
            benefit: benefitFilter,
            customerRule: customerFilter,
        },
        today,
    );
    const editOffer = useEditDeliveryOffer();
    const deleteOffer = useDeleteDeliveryOffer();
    const toast = useRef(null);
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("delivery-offers", COLUMNS);

    const showMessage = (type, head, content) => {
        toast.current.show({
            severity: type,
            summary: head,
            detail: content,
            life: 3000,
        });
    };

    const toggleAddForm = () => {
        setShowAddForm((pre) => !pre);
    };

    const toggleEditForm = () => {
        setShowEditForm((pre) => !pre);
    };

    const setSelectedOfferValue = (offer) => {
        setSelectedOffer(offer);
    };

    const toggleDelete = () => {
        setShowDelete((pre) => !pre);
    };

    const toggleActive = async (offer) => {
        try {
            await editOffer.mutateAsync({ id: offer.id, active: !offer.active });
            showMessage(
                "success",
                "تم التعديل",
                offer.active ? "تم إيقاف العرض" : "تم تفعيل العرض",
            );
        } catch (err) {
            console.error("Error toggling delivery offer:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    const confirmDelete = async () => {
        try {
            await deleteOffer.mutateAsync(selectedOffer.id);
            showMessage("warn", "تم الحذف", "تم حذف العرض بنجاح");
            toggleDelete();
        } catch (err) {
            console.error("Error deleting delivery offer:", err);
            showMessage("error", "هناك خطأ", "حدث خطأ غير متوقع");
        }
    };

    return (
        <div className="items">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            {loading ? (
                <div className="loadingSpinner">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : (
                <>
                    <div className="controls">
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", flex: 1 }}>
                            <button className="button" onClick={toggleAddForm}>
                                إضافه عرض توصيل
                            </button>
                            {/* The three offers that were a setting of a
                                restaurant or of a city. The choice opens the
                                add form with its fields set. */}
                            <Form.Select
                                aria-label="إضافه عرض جاهز"
                                value=""
                                onChange={(e) => openPreset(e.target.value)}
                                style={FILTER_STYLE}
                            >
                                <option value="">إضافه عرض جاهز…</option>
                                {Object.entries(OFFER_PRESETS).map(([key, preset]) => (
                                    <option key={key} value={key}>
                                        {preset.label}
                                    </option>
                                ))}
                            </Form.Select>
                            {adminInfo?.super_admin ? (
                                <Form.Select
                                    aria-label="المدينة"
                                    value={cityFilter}
                                    onChange={(e) => setCityFilter(e.target.value)}
                                    style={FILTER_STYLE}
                                >
                                    <option value="">المدينة: الكل</option>
                                    {cities?.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {city.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            ) : null}
                            <Form.Select
                                aria-label="الحاله"
                                value={stateFilter}
                                onChange={(e) => setStateFilter(e.target.value)}
                                style={FILTER_STYLE}
                                title="الحاله اليوم حسب تاريخ العرض ومفتاح التفعيل"
                            >
                                <option value="">الحاله: الكل</option>
                                {Object.entries(OFFER_STATE_PILL).map(([state, pill]) => (
                                    <option key={state} value={state}>
                                        الحاله: {pill.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                aria-label="نوع العرض"
                                value={benefitFilter}
                                onChange={(e) => setBenefitFilter(e.target.value)}
                                style={FILTER_STYLE}
                            >
                                <option value="">النوع: الكل</option>
                                {BENEFIT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                aria-label="الزبائن"
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                                style={FILTER_STYLE}
                            >
                                <option value="">الزبائن: الكل</option>
                                {CUSTOMER_RULE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <SearchInput
                                onSearch={setSearch}
                                placeholder="بحث باسم العرض أو المطعم"
                                minWidth={200}
                            />
                        </div>
                        <div style={{ marginInlineStart: "auto" }}>
                            <TableColumnsMenu
                                orderedColumns={orderedColumns}
                                hiddenSet={hiddenSet}
                                toggleColumn={toggleColumn}
                                moveColumn={moveColumn}
                                reset={reset}
                            />
                        </div>
                    </div>
                    <div className="table-container">
                        <table>
                            <TableHead visibleColumns={visibleColumns} />
                            <tbody>
                                {shownOffers.length > 0 ? (
                                    shownOffers.map((offer) => (
                                        <DeliveryOfferRow
                                            key={offer.id}
                                            offer={offer}
                                            today={today}
                                            visibleColumns={visibleColumns}
                                            toggleActive={toggleActive}
                                            toggleEditForm={toggleEditForm}
                                            setSelectedOfferValue={setSelectedOfferValue}
                                            toggleDelete={toggleDelete}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={visibleColumns.length}>
                                            {offers.length > 0
                                                ? "لا يوجد عرض بهذه الفلاتر"
                                                : "لا يوجد عروض توصيل!"}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <AddDeliveryOffer
                        show={showAddForm}
                        close={toggleAddForm}
                        showMessage={showMessage}
                        start={start}
                    />
                    <EditDeliveryOffer
                        show={showEditForm}
                        close={toggleEditForm}
                        showMessage={showMessage}
                        offer={selectedOffer}
                    />
                    <DeleteItem
                        show={showDelete}
                        close={toggleDelete}
                        loading={deleteOffer.isPending}
                        confirm={confirmDelete}
                    />
                </>
            )}
        </div>
    );
};
export default DeliveryOffers;
